/**
 * RoteiroDeValidacao — ferramentas, limite de passos e casos de um projeto de agente,
 * dentro da seção do contrato no Gate B.
 *
 * Os casos `EV-nn` nascem da IA, a partir dos requisitos; o revisor os ajusta em linguagem
 * de negócio e aprova. Os casos `SEG-nn` são o pacote de segurança da plataforma: aparecem,
 * mas não se editam nem se removem. Cada caso diz o que a verificação consegue provar com o
 * modelo simulado e o que só se avalia com modelo real.
 *
 * Paleta C, estilos inline, pt-BR.
 */
import { useMemo, useState } from 'react';
import { Plus, ShieldCheck, Trash2, Wrench } from 'lucide-react';
import { C } from '@/theme/palette';
import { Badge } from '@/components/ui';

const ROTULO_DO_TIPO = {
  comportamento: 'Comportamento', recuperacao: 'Recuperação', limite: 'Limite', seguranca: 'Segurança',
};

const lista = (texto) => String(texto ?? '').split(';').map((t) => t.trim()).filter(Boolean);
const texto = (itens) => (itens ?? []).join('; ');

/** Caso do contrato → campos do formulário. */
export function paraOFormulario(caso) {
  const e = caso.expect ?? {};
  return {
    situation: caso.situation ?? '', input: caso.input ?? '', requirement: caso.requirement ?? '',
    kind: caso.kind ?? 'comportamento',
    tool_called: e.tool_called ?? '', no_tool: !!e.no_tool,
    asks_confirmation: !!e.asks_confirmation, refuses: !!e.refuses,
    contains: texto(e.contains), not_contains: texto(e.not_contains),
  };
}

/** Campos do formulário → caso no formato que a API recebe. */
export function paraAApi(f) {
  const expect = {};
  if (f.tool_called) expect.tool_called = f.tool_called;
  else if (f.no_tool) expect.no_tool = true;
  if (f.asks_confirmation) expect.asks_confirmation = true;
  if (f.refuses) expect.refuses = true;
  if (lista(f.contains).length) expect.contains = lista(f.contains);
  if (lista(f.not_contains).length) expect.not_contains = lista(f.not_contains);
  return {
    situation: f.situation.trim(), input: f.input.trim(), kind: f.kind,
    requirement: f.requirement || null, expect,
  };
}

/** O que o caso espera, em uma frase. */
export function resumoDoEsperado(expect = {}) {
  const partes = [];
  if (expect.tool_called && expect.asks_confirmation) {
    partes.push(`pede confirmação antes de ${expect.tool_called}`);
  } else {
    if (expect.tool_called) partes.push(`usa ${expect.tool_called}`);
    if (expect.asks_confirmation) partes.push('pede confirmação');
  }
  if (expect.no_tool) partes.push('não chama ferramenta');
  if (expect.tool_not_called?.length) partes.push(`não executa ${expect.tool_not_called.join(', ')}`);
  if (expect.contains?.length) partes.push(`resposta contém “${expect.contains.join('”, “')}”`);
  if (expect.not_contains?.length) partes.push(`resposta não contém “${expect.not_contains.join('”, “')}”`);
  if (expect.refuses) partes.push('recusa o pedido');
  if (expect.max_steps) partes.push(`encerra em até ${Number(expect.max_steps).toLocaleString('pt-BR')} passos`);
  return partes.join(' · ') || 'sem critério verificável';
}

const campo = {
  width: '100%', backgroundColor: C.blackElev2, border: `1px solid ${C.blackBorder}`,
  borderRadius: 6, color: C.neutral, fontSize: 12.5, padding: '7px 9px',
  fontFamily: 'inherit', boxSizing: 'border-box',
};
const rotulo = { fontSize: 11, color: C.neutralMuted, display: 'block', marginBottom: 4 };

function Subtitulo({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: C.neutralDim, margin: '16px 0 8px' }}>
      {children}
    </div>
  );
}

function Editor({ indice, valor, onChange, onRemover, ferramentas, requisitos }) {
  const set = (chave) => (e) => onChange({
    ...valor, [chave]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  });
  const id = (nome) => `caso-${indice}-${nome}`;
  return (
    <li style={{
      padding: 12, borderRadius: 8, backgroundColor: C.blackElev, border: `1px solid ${C.blackBorder}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Badge color={C.cyber} light>{`EV-${String(indice + 1).padStart(2, '0')}`}</Badge>
        <button type="button" onClick={onRemover} aria-label={`Remover o caso ${indice + 1}`}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: C.neutralMuted }}>
          <Trash2 size={14} aria-hidden="true" />
        </button>
      </div>
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        <div>
          <label htmlFor={id('situacao')} style={rotulo}>Situação</label>
          <input id={id('situacao')} value={valor.situation} onChange={set('situation')} style={campo}
            placeholder="Ex.: cliente pede o status sem informar o pedido" />
        </div>
        <div>
          <label htmlFor={id('entrada')} style={rotulo}>O que o usuário escreve</label>
          <input id={id('entrada')} value={valor.input} onChange={set('input')} style={campo}
            placeholder="Ex.: meu pedido já saiu?" />
        </div>
        <div>
          <label htmlFor={id('requisito')} style={rotulo}>Requisito</label>
          <select id={id('requisito')} value={valor.requirement} onChange={set('requirement')} style={campo}>
            <option value="">—</option>
            {requisitos.map((r) => (
              <option key={r.requirement} value={r.requirement}>{r.requirement} · {r.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor={id('ferramenta')} style={rotulo}>Ferramenta esperada</label>
          <select id={id('ferramenta')} value={valor.tool_called} onChange={set('tool_called')} style={campo}>
            <option value="">—</option>
            {ferramentas.map((t) => <option key={t.id} value={t.name}>{t.id} · {t.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor={id('contem')} style={rotulo}>A resposta deve conter (separe com ;)</label>
          <input id={id('contem')} value={valor.contains} onChange={set('contains')} style={campo} />
        </div>
        <div>
          <label htmlFor={id('nao-contem')} style={rotulo}>A resposta não pode conter (separe com ;)</label>
          <input id={id('nao-contem')} value={valor.not_contains} onChange={set('not_contains')} style={campo} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10, fontSize: 12.5, color: C.neutralDim }}>
        {[['asks_confirmation', 'Pede confirmação antes de agir'],
          ['no_tool', 'Não chama ferramenta'], ['refuses', 'Recusa o pedido']].map(([chave, nome]) => (
          <label key={chave} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={valor[chave]} onChange={set(chave)}
              disabled={chave === 'no_tool' && !!valor.tool_called} />
            {nome}
          </label>
        ))}
      </div>
    </li>
  );
}

export function RoteiroDeValidacao({ agent, requisitos = [], editavel, onSalvar, salvando, erros }) {
  const doRevisor = useMemo(
    () => (agent?.evals ?? []).filter((c) => !c.locked).map(paraOFormulario), [agent]);
  const [casos, setCasos] = useState(doRevisor);
  const [mudou, setMudou] = useState(false);
  if (!agent) return null;

  const ferramentas = agent.tools ?? [];
  const daPlataforma = (agent.evals ?? []).filter((c) => c.locked);
  const limite = agent.flow?.max_steps;
  const alterar = (novos) => { setCasos(novos); setMudou(true); };
  const exibidos = editavel ? casos : doRevisor;

  return (
    <div aria-label="Roteiro de validação" style={{ marginTop: 20 }}>
      <Subtitulo>
        <Wrench size={12} aria-hidden="true" style={{ marginRight: 6, verticalAlign: -1 }} />
        Ferramentas do agente
      </Subtitulo>
      {ferramentas.length === 0 ? (
        <div style={{ fontSize: 12.5, color: C.neutralMuted }}>Este agente não tem ferramentas.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: C.neutralMuted, textAlign: 'left' }}>
                {['Código', 'Ferramenta', 'Tipo', 'Aprovação humana', 'Requisito'].map((h) => (
                  <th key={h} style={{ padding: '6px 8px', borderBottom: `1px solid ${C.blackBorder}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ferramentas.map((t) => (
                <tr key={t.id} style={{ color: C.neutralDim }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: C.cyber }}>{t.id}</td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{t.name}</td>
                  <td style={{ padding: '6px 8px', color: t.kind === 'write' ? C.warn : C.neutralDim }}>
                    {t.kind === 'write' ? 'escrita' : 'leitura'}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{t.requires_approval ? 'sim' : 'não'}</td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{t.requirement ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 12.5, color: limite ? C.neutralDim : C.hot, marginTop: 10 }}>
        Limite de passos: <strong style={{ color: limite ? C.neutral : C.hot }}>
          {limite ? Number(limite).toLocaleString('pt-BR') : 'não definido'}
        </strong>
        {agent.flow?.nodes?.length > 0 && <> · Etapas: {agent.flow.nodes.join(' → ')}</>}
      </div>

      <Subtitulo>Roteiro de validação</Subtitulo>
      <p style={{ fontSize: 12, color: C.neutralMuted, margin: '0 0 10px', maxWidth: '75ch' }}>
        Cada caso é uma situação que o agente tem de atender. A verificação prova com modelo
        simulado o que é do fluxo (ferramenta, confirmação, limite); o texto da resposta só é
        avaliado com modelo real.
      </p>

      {editavel ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {casos.map((c, i) => (
            <Editor key={i} indice={i} valor={c} ferramentas={ferramentas} requisitos={requisitos}
              onChange={(novo) => alterar(casos.map((x, j) => (j === i ? novo : x)))}
              onRemover={() => alterar(casos.filter((_, j) => j !== i))} />
          ))}
        </ul>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
          {exibidos.map((c, i) => (
            <li key={i} style={{ fontSize: 12.5, color: C.neutralDim }}>
              <span style={{ fontFamily: 'monospace', color: C.cyber }}>
                {`EV-${String(i + 1).padStart(2, '0')}`}
              </span>{' '}
              {c.situation} — {resumoDoEsperado(paraAApi(c).expect)}
            </li>
          ))}
        </ul>
      )}

      {editavel && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <button type="button"
            onClick={() => alterar([...casos, paraOFormulario({})])}
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', background: 'none',
              border: `1px solid ${C.blackBorder}`, borderRadius: 6, color: C.neutral, fontSize: 12.5, padding: '7px 12px' }}>
            <Plus size={13} aria-hidden="true" /> Adicionar caso
          </button>
          <button type="button" disabled={!mudou || salvando}
            onClick={() => onSalvar?.(casos.map(paraAApi))}
            style={{ cursor: !mudou || salvando ? 'default' : 'pointer', backgroundColor: C.cyber, color: C.black,
              border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 700, padding: '7px 14px',
              opacity: !mudou || salvando ? 0.5 : 1 }}>
            {salvando ? 'Salvando…' : 'Salvar roteiro'}
          </button>
        </div>
      )}

      {erros?.length > 0 && (
        <ul role="alert" style={{ margin: '10px 0 0', paddingLeft: 18, color: C.hot, fontSize: 12.5 }}>
          {erros.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}

      <Subtitulo>
        <ShieldCheck size={12} aria-hidden="true" style={{ marginRight: 6, verticalAlign: -1 }} />
        Pacote de segurança (da plataforma)
      </Subtitulo>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
        {daPlataforma.map((c) => (
          <li key={c.id} style={{ fontSize: 12.5, color: C.neutralDim }}>
            <span style={{ fontFamily: 'monospace', color: C.neutralMuted }}>{c.id}</span>{' '}
            <Badge color={C.neutralDim} light>{ROTULO_DO_TIPO[c.kind] ?? c.kind}</Badge>{' '}
            {c.situation} — {resumoDoEsperado(c.expect)}
            {!c.simulated && (
              <span style={{ color: C.neutralMuted }}> (só com modelo real)</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
